import React from 'react';
import { XOutlined } from '@ant-design/icons';
import { useClub } from 'src/context/useClub';
import { FacebookLogo, InstagramLogo, YoutubeLogo } from '../assets';
import css from './Hero.module.scss';
import { cx } from 'src/utils/styles';

type HeroTemplateKey = 'classic' | 'community' | 'split' | 'minimal' | 'dynamic' | 'video' | 'carousel';

const TEMPLATE_KEYS: HeroTemplateKey[] = ['classic', 'community', 'split', 'minimal', 'dynamic', 'video', 'carousel'];

function normalizeUrl(value?: string | null): string {
  if (!value) return '';
  const trimmed = value.trim();
  if (!trimmed) return '';
  if (trimmed.startsWith('/') || trimmed.startsWith('#')) return trimmed;
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
}

function parseCarouselImages(value: string): string[] {
  const trimmed = value.trim();
  if (!trimmed) return [];

  try {
    const parsed = JSON.parse(trimmed);
    if (Array.isArray(parsed)) {
      return parsed
        .map(item => (typeof item === 'string' ? item.trim() : ''))
        .filter(Boolean);
    }
  } catch {
    // Fallback to delimiter parsing.
  }

  return trimmed
    .split(/[\n,]/)
    .map(item => item.trim())
    .filter(Boolean);
}

function clampOpacity(value: number): number {
  if (Number.isNaN(value)) return 0.45;
  if (value < 0) return 0;
  if (value > 0.85) return 0.85;
  return value;
}

function getYouTubeVideoId(value: string): string | null {
  try {
    const url = new URL(value);
    const host = url.hostname.toLowerCase().replace(/^www\./, '');

    if (host === 'youtu.be') {
      return url.pathname.split('/').filter(Boolean)[0] || null;
    }

    if (!host.includes('youtube.com') && host !== 'youtube-nocookie.com') {
      return null;
    }

    const fromQuery = url.searchParams.get('v');
    if (fromQuery) return fromQuery;

    const parts = url.pathname.split('/').filter(Boolean);
    const markerIndex = parts.findIndex(part => part === 'embed' || part === 'shorts' || part === 'live');
    if (markerIndex >= 0 && parts[markerIndex + 1]) {
      return parts[markerIndex + 1];
    }
  } catch {
    return null;
  }

  return null;
}

function toYouTubeEmbedUrl(value: string): string {
  const videoId = getYouTubeVideoId(value);
  if (!videoId) return '';

  const params = new URLSearchParams({
    autoplay: '1',
    controls: '0',
    loop: '1',
    modestbranding: '1',
    mute: '1',
    playsinline: '1',
    playlist: videoId,
    rel: '0',
  });

  return `https://www.youtube.com/embed/${videoId}?${params.toString()}`;
}

function usePrefersReducedMotion(): boolean {
  const [prefersReducedMotion, setPrefersReducedMotion] = React.useState(false);

  React.useEffect(() => {
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setPrefersReducedMotion(media.matches);
    update();

    if (media.addEventListener) {
      media.addEventListener('change', update);
      return () => media.removeEventListener('change', update);
    }

    media.addListener(update);
    return () => media.removeListener(update);
  }, []);

  return prefersReducedMotion;
}

function HeroActions({
  primaryLabel,
  primaryUrl,
  secondaryLabel,
  secondaryUrl,
  showPrimary,
  showSecondary,
  dark,
}: {
  primaryLabel: string;
  primaryUrl: string;
  secondaryLabel: string;
  secondaryUrl: string;
  showPrimary: boolean;
  showSecondary: boolean;
  dark?: boolean;
}): JSX.Element | null {
  if (!showPrimary && !showSecondary) return null;

  return (
    <div className={css.actions}>
      {showPrimary ? (
        <a href={normalizeUrl(primaryUrl) || '#'} className={cx(css.action, css.primaryAction, dark && css.darkPrimary)}>
          {primaryLabel}
        </a>
      ) : null}
      {showSecondary ? (
        <a
          href={normalizeUrl(secondaryUrl) || '#'}
          className={cx(css.action, css.secondaryAction, dark && css.darkSecondary)}
        >
          {secondaryLabel}
        </a>
      ) : null}
    </div>
  );
}

export const Hero: React.FC = () => {
  const {
    club: { info },
  } = useClub();
  const prefersReducedMotion = usePrefersReducedMotion();

  const heroTemplate: HeroTemplateKey = TEMPLATE_KEYS.includes(info.heroTemplate as HeroTemplateKey)
    ? (info.heroTemplate as HeroTemplateKey)
    : 'classic';

  const heroImageUrl = info.headerUrl || '';
  const logoUrl = info.logoUrl || '/assets/img/shield_place_holder.png';
  const carouselImages = parseCarouselImages(info.heroCarouselImages);
  const normalizedVideoUrl = normalizeUrl(info.heroVideoUrl);
  const youtubeEmbedUrl = toYouTubeEmbedUrl(normalizedVideoUrl);
  const isYouTubeVideo = youtubeEmbedUrl.length > 0;

  const [videoFailed, setVideoFailed] = React.useState(false);
  const [carouselIndex, setCarouselIndex] = React.useState(0);

  const canUseVideo = info.heroEnableVideoBackground && !!normalizedVideoUrl && !videoFailed && !prefersReducedMotion;
  const canUseCarousel = info.heroEnableCarousel && carouselImages.length >= 2;

  let effectiveTemplate = heroTemplate;
  if (effectiveTemplate === 'video' && !canUseVideo) {
    effectiveTemplate = heroImageUrl ? 'classic' : 'minimal';
  }
  if (effectiveTemplate === 'carousel' && !canUseCarousel && carouselImages.length < 1) {
    effectiveTemplate = heroImageUrl ? 'classic' : 'minimal';
  }
  if (!heroImageUrl && !canUseVideo && carouselImages.length === 0 && effectiveTemplate !== 'minimal') {
    effectiveTemplate = 'minimal';
  }

  const safeOpacity = clampOpacity(info.heroOverlayOpacity);
  const textAlignment = info.heroTextAlignment === 'center' ? css.center : css.left;

  const heroTitle = info.heroTitle?.trim() || info.name;
  const heroSubtitle = info.heroSubtitle?.trim() || info.tagline;
  const primaryText = info.heroPrimaryButtonText?.trim() || 'Join the Club';
  const secondaryText = info.heroSecondaryButtonText?.trim() || 'View Teams';

  const showPrimary = info.heroShowPrimaryButton && primaryText.length > 0;
  const showSecondary = info.heroShowSecondaryButton && secondaryText.length > 0;

  const carouselInterval = Math.min(30000, Math.max(1000, info.heroCarouselIntervalMs || 5000));

  React.useEffect(() => {
    setVideoFailed(false);
  }, [info.heroVideoUrl]);

  React.useEffect(() => {
    setCarouselIndex(0);
  }, [info.heroCarouselImages]);

  React.useEffect(() => {
    if (effectiveTemplate !== 'carousel') return;
    if (!canUseCarousel) return;
    if (prefersReducedMotion) return;

    const interval = window.setInterval(() => {
      setCarouselIndex(currentIndex => (currentIndex + 1) % carouselImages.length);
    }, carouselInterval);

    return () => window.clearInterval(interval);
  }, [effectiveTemplate, canUseCarousel, prefersReducedMotion, carouselImages.length, carouselInterval]);

  const carouselBackground =
    carouselImages.length > 0
      ? carouselImages[Math.min(carouselIndex, carouselImages.length - 1)]
      : heroImageUrl || '/assets/img/hero.png';

  const staticBackground = heroImageUrl || carouselImages[0] || '/assets/img/hero.png';

  return (
    <section
      className={cx(
        css.hero,
        css[effectiveTemplate],
        info.heroTextAlignment === 'center' && css.isCentered,
        prefersReducedMotion && css.reduceMotion,
      )}
      style={
        effectiveTemplate !== 'video'
          ? {
              backgroundImage: `url('${effectiveTemplate === 'carousel' ? carouselBackground : staticBackground}')`,
            }
          : undefined
      }
    >
      {effectiveTemplate === 'video' && canUseVideo ? (
        isYouTubeVideo ? (
          <iframe
            className={css.videoEmbedLayer}
            title={`${info.name} hero video`}
            src={youtubeEmbedUrl}
            allow='autoplay; encrypted-media; picture-in-picture'
            allowFullScreen={false}
            referrerPolicy='strict-origin-when-cross-origin'
          />
        ) : (
          <video
            className={css.videoLayer}
            autoPlay
            muted
            loop
            playsInline
            poster={info.heroVideoPosterUrl || staticBackground}
            onError={() => setVideoFailed(true)}
          >
            <source src={normalizedVideoUrl} />
          </video>
        )
      ) : null}

      <div className={css.overlay} style={{ backgroundColor: `rgba(7, 15, 29, ${safeOpacity})` }} />

      <div className={cx(css.content, textAlignment, effectiveTemplate === 'split' && css.splitLayout)}>
        <div className={css.contentPrimary}>
          {info.heroShowClubLogo ? <img className={css.logo} src={logoUrl} alt={`${info.name} logo`} /> : null}
          <p className={css.kicker}>{info.name}</p>
          <h1 className={css.title}>{heroTitle}</h1>
          <p className={css.subtitle}>{heroSubtitle}</p>
          <HeroActions
            primaryLabel={primaryText}
            primaryUrl={info.heroPrimaryButtonUrl}
            secondaryLabel={secondaryText}
            secondaryUrl={info.heroSecondaryButtonUrl}
            showPrimary={showPrimary}
            showSecondary={showSecondary}
            dark={effectiveTemplate === 'minimal'}
          />
        </div>

        {effectiveTemplate === 'split' ? (
          <div className={css.splitMedia} style={{ backgroundImage: `url('${staticBackground}')` }} />
        ) : null}
      </div>

      {effectiveTemplate === 'carousel' && carouselImages.length > 1 ? (
        <div className={css.carouselDots}>
          {carouselImages.map((_, idx) => (
            <span key={idx} className={cx(css.dot, idx === carouselIndex && css.active)} />
          ))}
        </div>
      ) : null}

      {effectiveTemplate === 'video' && canUseVideo ? <span className={css.videoBadge}>Live Video</span> : null}

      <div className={cx(css.social, 'position-absolute d-flex flex-column h-100')}>
        {info.instagramUrl && <InstagramLogo onClick={() => window.open(normalizeUrl(info.instagramUrl), '_blank')} />}
        {info.youtubeUrl && <YoutubeLogo onClick={() => window.open(normalizeUrl(info.youtubeUrl), '_blank')} />}
        {info.facebookUrl && <FacebookLogo onClick={() => window.open(normalizeUrl(info.facebookUrl), '_blank')} />}
        {info.twitterUrl && (
          <XOutlined
            style={{ fontSize: '18px', marginBottom: '2rem' }}
            onClick={() => window.open(normalizeUrl(info.twitterUrl), '_blank')}
          />
        )}
      </div>
    </section>
  );
};
